/*
  hj-frame-preview.js  (REFERENCE IMPLEMENTATION, POC, Paper only)

  Renders a live framed-and-matted preview of a bare-art image using four
  mitered rails so woodgrain runs lengthwise on each side, with per-material
  treatment (wood grain, gold paint, solid black/white) and a calm studio
  background. This is a SIMULATED preview, not a photo of the real product.

  Public API:
    HJFramePreview.render(container, {
      imgUrl,        // bare-art image URL (the hey_jack bare_art_url)
      sizeToken,     // one of: "8X12","12X18","16X24","24X36","30X45","40X60"
      frameKey,      // one of: "unframed","natural","oak","espresso","black","white","gold"
      mat,           // boolean: show mat (paper, framed, non-Giant only)
      maxPx          // fallback longest edge in px when the slot has no
                     // measurable size (default 520); the measured container
                     // size drives the scale whenever it is available
    });

  Notes for integration:
  - Paper only for this POC. Canvas and Digital are handled separately later.
  - "unframed" renders the bare art with a thin edge, no rails, no mat.
  - Giant (40X60) never mats, even if mat=true is passed.
  - No external dependencies. Pure DOM + CSS + inline SVG/canvas.
  - Colors and mat math below are the agreed Hey Jack values. Do not change
    without checking variants.ts and the confirmed 24x36 spec.
*/

(function (global) {
  "use strict";

  // ----- Agreed Hey Jack data -----
  // Three native ratios: 2:3, 4:5, 1:1. A product carries variants for one
  // ratio only; the engine renders whichever size the PDP currently has
  // selected. Mat thickness pattern: 2.5" on smaller sizes, 2.0" on mid
  // sizes, 0 on the Giant rectangles (40x60, 40x50) which do not mat. 1:1's
  // largest (36x36) keeps a 2.0" mat since mat IS allowed at every square
  // size.
  var SIZES = {
    // 2:3 sizes
    "8X12":  { imgW: 12, imgH: 8,  mat: 2.5 },
    "12X18": { imgW: 18, imgH: 12, mat: 2.5 },
    "16X24": { imgW: 24, imgH: 16, mat: 2.5 },
    "24X36": { imgW: 36, imgH: 24, mat: 2.0 },
    "30X45": { imgW: 45, imgH: 30, mat: 2.0 },
    "40X60": { imgW: 60, imgH: 40, mat: 0   },  // Giant: never mats
    // 4:5 sizes
    "8X10":  { imgW: 10, imgH: 8,  mat: 2.5 },
    "16X20": { imgW: 20, imgH: 16, mat: 2.5 },
    "24X30": { imgW: 30, imgH: 24, mat: 2.0 },
    "32X40": { imgW: 40, imgH: 32, mat: 2.0 },
    "40X50": { imgW: 50, imgH: 40, mat: 0   },  // Giant: never mats
    // 1:1 sizes
    "8X8":   { imgW: 8,  imgH: 8,  mat: 2.5 },
    "12X12": { imgW: 12, imgH: 12, mat: 2.5 },
    "16X16": { imgW: 16, imgH: 16, mat: 2.5 },
    "20X20": { imgW: 20, imgH: 20, mat: 2.5 },
    "24X24": { imgW: 24, imgH: 24, mat: 2.0 },
    "30X30": { imgW: 30, imgH: 30, mat: 2.0 },
    "36X36": { imgW: 36, imgH: 36, mat: 2.0 }   // 1:1 largest; mat still allowed
  };

  // Derived from the confirmed 24x36 spec.
  var MAT_OVERLAP = 0.125; // mat overlaps the image edge, per side (inches)

  // Frame colors. mat family: "wood" | "gold" | "solid". "unframed" handled separately.
  var COLORS = {
    natural:  { base: "#e0cda8", family: "wood"  },
    oak:      { base: "#b08d5b", family: "wood"  },
    espresso: { base: "#5a3c28", family: "wood"  },
    black:    { base: "#1c1a18", family: "solid", key: "black" },
    white:    { base: "#f0ece4", family: "solid", key: "white" },
    gold:     { base: "#caa64a", family: "gold"  }
  };

  // ----- color helpers -----
  function hexToRgb(h) {
    h = h.replace("#", "");
    if (h.length === 3) h = h.split("").map(function (x) { return x + x; }).join("");
    var n = parseInt(h, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  function rgbToHex(r, g, b) {
    return "#" + [r, g, b].map(function (v) {
      v = Math.max(0, Math.min(255, Math.round(v)));
      return v.toString(16).padStart(2, "0");
    }).join("");
  }
  function shade(hex, pct) {
    var c = hexToRgb(hex), f = 1 + pct / 100;
    return rgbToHex(c[0] * f, c[1] * f, c[2] * f);
  }
  function rgba(hex, a) {
    var c = hexToRgb(hex);
    return "rgba(" + c[0] + "," + c[1] + "," + c[2] + "," + a + ")";
  }

  // SVG fractal-noise woodgrain, tinted toward the base color, grain runs
  // horizontally (lengthwise on a rail). Returns a url() string.
  function svgWood(base) {
    var c = hexToRgb(base);
    var m =
      (c[0] / 255 * 1.1).toFixed(2) + " 0 0 0 0  " +
      (c[1] / 255 * 0.95).toFixed(2) + " 0 0 0 0  " +
      (c[2] / 255 * 0.85).toFixed(2) + " 0 0 0 0  " +
      "0 0 0 0.55 0";
    var svg =
      "<svg xmlns='http://www.w3.org/2000/svg' width='240' height='80'>" +
      "<filter id='w'>" +
      "<feTurbulence type='fractalNoise' baseFrequency='0.012 0.16' numOctaves='4' seed='7' result='n'/>" +
      "<feColorMatrix in='n' type='matrix' values='" + m + "'/>" +
      "</filter>" +
      "<rect width='240' height='80' filter='url(#w)' opacity='0.9'/>" +
      "</svg>";
    return "url(\"data:image/svg+xml;utf8," + encodeURIComponent(svg) + "\")";
  }

  // ----- per-material rail fill -----
  function fillWood(grain, col) {
    grain.style.backgroundColor = col.base;
    grain.style.backgroundImage = svgWood(col.base);
    grain.style.backgroundSize = "240px 100%";
  }
  function fillSolid(grain, col) {
    var hi = shade(col.base, col.key === "white" ? 4 : 18);
    var lo = shade(col.base, col.key === "white" ? -6 : -22);
    grain.style.background = "linear-gradient(0deg, " + lo + ", " + col.base + " 45%, " + hi + ")";
  }
  function fillGold(grain, col) {
    var b = col.base;
    var dark = shade(b, -16);       // soft shaded edge, gentle not deep
    var pale = shade(b, 14);        // soft warm sheen, no specular hotspot
    var grainTint = shade(b, -50);  // dark brown grain, well below the gold so it reads
    // Gold-painted wood: an opaque satin gold base (low-contrast 0deg gradient)
    // with the wood frames' lengthwise grain multiplied on top, so the dark
    // grain darkens the gold exactly where the wood lines are, the way grain
    // telegraphs through a coat of gold paint. Multiply (not a faint overlay) is
    // what makes the grain actually visible. No specular band, so it is painted
    // wood, not metal.
    var coat =
      "linear-gradient(0deg, " +
        dark + " 0%, " +
        b + " 32%, " +
        pale + " 56%, " +
        b + " 80%, " +
        shade(b, -10) + " 100%)";
    grain.style.backgroundColor = b;
    grain.style.backgroundImage = svgWood(grainTint) + ", " + coat;
    grain.style.backgroundBlendMode = "multiply, normal";
    grain.style.backgroundSize = "240px 100%, 100% 100%";
    grain.style.filter = "saturate(1.05)";
  }
  function fillRail(grain, col) {
    if (col.family === "solid") return fillSolid(grain, col);
    if (col.family === "gold") return fillGold(grain, col);
    return fillWood(grain, col);
  }
  // Solids always carve (so they are not flat borders); woods and gold get a
  // soft lip only. For the storefront we always apply the soft carved lip.
  function carveStrong(col) { return col.family === "solid"; }

  // ----- styles, injected once -----
  var STYLE_ID = "hj-frame-preview-style";
  function ensureStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var css =
      ".hjfp-stage{position:relative;display:flex;align-items:center;justify-content:center;" +
        "padding:0;border-radius:8px;min-height:0;box-sizing:border-box;" +
        "background:linear-gradient(135deg,#efe9dd 0%,#e7dfd0 100%);}" +
      ".hjfp-frame{position:relative;border-radius:2px;" +
        "box-shadow:0 12px 30px rgba(0,0,0,.24),0 2px 6px rgba(0,0,0,.18);}" +
      ".hjfp-rail{position:absolute;overflow:hidden;" +
        "box-shadow:inset 0 0 0 1px rgba(255,255,255,.07);}" +
      ".hjfp-grain{position:absolute;top:50%;left:50%;transform-origin:center center;}" +
      ".hjfp-lit{position:absolute;inset:0;pointer-events:none;}" +
      ".hjfp-frame .hjfp-rail.top .hjfp-lit{box-shadow:inset 0 -3px 4px rgba(0,0,0,.30),inset 0 2px 2px rgba(255,255,255,.22);}" +
      ".hjfp-frame .hjfp-rail.bottom .hjfp-lit{box-shadow:inset 0 3px 5px rgba(0,0,0,.24),inset 0 -2px 2px rgba(255,255,255,.12);}" +
      ".hjfp-frame .hjfp-rail.left .hjfp-lit{box-shadow:inset -3px 0 4px rgba(0,0,0,.26),inset 2px 0 2px rgba(255,255,255,.20);}" +
      ".hjfp-frame .hjfp-rail.right .hjfp-lit{box-shadow:inset 3px 0 5px rgba(0,0,0,.24),inset -2px 0 2px rgba(255,255,255,.10);}" +
      ".hjfp-frame.carve .hjfp-rail.top .hjfp-lit{box-shadow:inset 0 -3px 4px rgba(0,0,0,.38),inset 0 2px 2px rgba(255,255,255,.30);}" +
      ".hjfp-frame.carve .hjfp-rail.bottom .hjfp-lit{box-shadow:inset 0 3px 5px rgba(0,0,0,.30),inset 0 -2px 2px rgba(255,255,255,.16);}" +
      ".hjfp-frame.carve .hjfp-rail.left .hjfp-lit{box-shadow:inset -3px 0 4px rgba(0,0,0,.34),inset 2px 0 2px rgba(255,255,255,.26);}" +
      ".hjfp-frame.carve .hjfp-rail.right .hjfp-lit{box-shadow:inset 3px 0 5px rgba(0,0,0,.30),inset -2px 0 2px rgba(255,255,255,.14);}" +
      ".hjfp-frame.gold .hjfp-rail.top .hjfp-lit{box-shadow:inset 0 -1px 1px rgba(0,0,0,.05),inset 0 1px 1px rgba(255,255,255,.07);}" +
      ".hjfp-frame.gold .hjfp-rail.bottom .hjfp-lit{box-shadow:inset 0 1px 1px rgba(0,0,0,.05),inset 0 -1px 1px rgba(255,255,255,.05);}" +
      ".hjfp-frame.gold .hjfp-rail.left .hjfp-lit{box-shadow:inset -1px 0 1px rgba(0,0,0,.05),inset 1px 0 1px rgba(255,255,255,.06);}" +
      ".hjfp-frame.gold .hjfp-rail.right .hjfp-lit{box-shadow:inset 1px 0 1px rgba(0,0,0,.05),inset -1px 0 1px rgba(255,255,255,.04);}" +
      ".hjfp-mat{position:absolute;background:#f7f4ee;" +
        "box-shadow:inset 0 0 0 1px rgba(0,0,0,.05),inset 2px 2px 5px rgba(0,0,0,.10);}" +
      ".hjfp-art{position:absolute;overflow:hidden;box-shadow:inset 0 0 0 1px rgba(0,0,0,.18);}" +
      ".hjfp-art img{width:100%;height:100%;object-fit:cover;display:block;}";
    var el = document.createElement("style");
    el.id = STYLE_ID;
    el.textContent = css;
    document.head.appendChild(el);
  }

  // ----- responsive sizing -----
  // The preview fills its gallery slot instead of a fixed pixel size. render()
  // measures the container it is handed (falling back to its parent, then to
  // opts.maxPx when nothing is measurable, e.g. the slot is still hidden on the
  // first call) and recomputes the scale on resize, orientation change, or when
  // the slot becomes visible. maxPx is kept only as that fallback, not the
  // primary size driver, so existing render() calls keep working.
  var _last = null;                 // { container, opts } for re-render
  var _lastSize = { w: 0, h: 0 };   // last measured slot size, to skip no-ops
  var _reflowTimer = null;
  var _resizeBound = false;
  var _bananaClicks = 0;            // banana link clicks so far (module scope so
                                    // the gag's escalation survives re-renders):
                                    // 1 -> Reddit, 2 -> NPR, 3+ -> rain bananas

  function measureAvail(el) {
    var w = 0, h = 0;
    if (el) {
      w = el.clientWidth; h = el.clientHeight;
      if ((!w || !h) && el.getBoundingClientRect) {
        var r = el.getBoundingClientRect();
        w = w || Math.round(r.width); h = h || Math.round(r.height);
      }
      if ((!w || !h) && el.parentElement) {
        w = w || el.parentElement.clientWidth;
        h = h || el.parentElement.clientHeight;
      }
    }
    return { w: w, h: h };
  }

  // Lightly debounced re-render from the last container + opts. Recomputes the
  // scale for the new container size while preserving the selected
  // size/frame/mat (opts are unchanged). Skips when nothing visible/changed.
  function scheduleReflow() {
    if (_reflowTimer) return;
    _reflowTimer = setTimeout(function () {
      _reflowTimer = null;
      if (!_last || !_last.container) return;
      var a = measureAvail(_last.container);
      if (a.w > 1 && a.h > 1 && (a.w !== _lastSize.w || a.h !== _lastSize.h)) {
        render(_last.container, _last.opts);
      }
    }, 120);
  }
  function bindResize() {
    if (_resizeBound) return;
    _resizeBound = true;
    window.addEventListener("resize", scheduleReflow);
    window.addEventListener("orientationchange", scheduleReflow);
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  }

  // Open a URL in a BACKGROUND tab, keeping the current page focused, the way a
  // manual cmd/ctrl + click does. window.open steals focus to the new tab, which
  // is not what we want here, so instead synthesize a modifier + left click on a
  // throwaway anchor: the browser actions that as "open in new background tab"
  // and never moves the user off the page. rel hardens the new tab against the
  // opener. Falls back to window.open if the synthetic click path is unavailable.
  function openInNewTab(url) {
    try {
      var a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.style.display = "none";
      document.body.appendChild(a);
      var isMac = /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent || "");
      var ev = new MouseEvent("click", {
        bubbles: true,
        cancelable: true,
        view: window,
        button: 0,
        ctrlKey: !isMac, // ctrl is the "new tab" modifier off Mac
        metaKey: isMac   // cmd is the "new tab" modifier on Mac
      });
      a.dispatchEvent(ev);
      window.setTimeout(function () { a.remove(); }, 0);
    } catch (err) {
      var w = window.open(url, "_blank");
      if (w) { w.opener = null; }
    }
  }

  // Easter egg for the third banana click: a short burst of banana emoji raining
  // down the viewport. Pure Web Animations API (no injected CSS), each emoji is
  // fixed-positioned with pointer-events:none so it never shifts layout or eats
  // clicks, and removes itself when its fall finishes. Falls back to a plain
  // timed removal if animate() is unavailable.
  function rainBananas() {
    if (typeof document === "undefined" || !document.body) return;
    var COUNT = 240; // a ton of bananas
    var vh = window.innerHeight || 800;
    for (var i = 0; i < COUNT; i++) {
      (function () {
        var b = document.createElement("span");
        b.textContent = "🍌"; // banana emoji
        b.setAttribute("aria-hidden", "true");
        b.style.position = "fixed";
        b.style.top = "-10vh";
        b.style.left = (Math.random() * 100) + "vw";
        b.style.fontSize = (18 + Math.random() * 30) + "px";
        b.style.lineHeight = "1";
        b.style.pointerEvents = "none";
        b.style.userSelect = "none";
        b.style.zIndex = "2147483647";
        b.style.willChange = "transform, opacity";
        document.body.appendChild(b);

        var dx = (Math.random() * 160 - 80);
        var rot = (Math.random() * 720 - 360);
        var dur = 2600 + Math.random() * 2400;
        var delay = Math.random() * 2200; // spread the downpour out over time
        var fall = (typeof b.animate === "function")
          ? b.animate(
              [
                { transform: "translate(0, 0) rotate(0deg)", opacity: 1 },
                { transform: "translate(" + dx + "px, " + (vh + 160) + "px) rotate(" + rot + "deg)", opacity: 1, offset: 0.88 },
                { transform: "translate(" + dx + "px, " + (vh + 200) + "px) rotate(" + rot + "deg)", opacity: 0 }
              ],
              { duration: dur, delay: delay, easing: "cubic-bezier(0.45, 0.05, 0.55, 1)", fill: "forwards" }
            )
          : null;
        if (fall) { fall.onfinish = function () { b.remove(); }; }
        else { window.setTimeout(function () { b.remove(); }, 4000); }
      })();
    }
  }

  // Anchor is the largest size offered for this product (largest finished
  // area by long-edge inches). Derived from the visible size cards on the
  // PDP so the engine adapts to the product's native ratio (2:3, 4:5, or
  // 1:1) without needing to know the ratio explicitly. Falls back to 40X60
  // if no recognised size cards are found, which preserves prior behaviour.
  function getAnchorSize() {
    var cards = document.querySelectorAll('[data-optsel-card][data-size]');
    var maxKey = null;
    var maxLong = 0;
    cards.forEach(function (card) {
      var key = (card.getAttribute('data-size') || '').toUpperCase();
      if (!SIZES[key]) return;
      var s = SIZES[key];
      var longSide = Math.max(s.imgW, s.imgH);
      if (longSide > maxLong) { maxLong = longSide; maxKey = key; }
    });
    return maxKey ? SIZES[maxKey] : SIZES["40X60"];
  }

  // ----- main render -----
  function render(container, opts) {
    ensureStyle();
    var size = SIZES[opts.sizeToken];
    if (!size) { console.warn("HJFramePreview: unknown size", opts.sizeToken); return; }

    // Remember the latest container + opts so a resize, orientation change, or
    // the slot becoming visible can re-render at the right size. A ResizeObserver
    // covers the hidden-at-first-render case: the slot reports zero until shown,
    // then fires once it gains a real size and we re-render to fill it.
    _last = { container: container, opts: opts };
    bindResize();
    if (window.ResizeObserver && container && !container.__hjpRO) {
      container.__hjpRO = new ResizeObserver(function () { scheduleReflow(); });
      container.__hjpRO.observe(container);
    }

    var maxPx = opts.maxPx || 520;
    var unframed = opts.frameKey === "unframed";
    var col = COLORS[opts.frameKey] || null;

    // Create the artwork image up front so its natural orientation can be read.
    var img = document.createElement("img");
    img.alt = "Framed preview";
    img.src = opts.imgUrl;
    // Portrait when the art is taller than wide. If the image has not decoded
    // yet, build with the token's default (landscape) orientation now and
    // rebuild once the natural size is known. A cached image is already complete
    // so its orientation is used immediately, with no rebuild.
    var known = img.complete && img.naturalWidth > 0;
    if (!known) { img.addEventListener("load", function () { render(container, opts); }); }
    var portrait = known && img.naturalHeight > img.naturalWidth;

    // The size token carries two numbers (e.g. 24X36). Keep both, but assign the
    // longer side to follow the art: portrait art gets the longer side as height,
    // landscape keeps the longer side as width (the token's default orientation).
    var longSide = Math.max(size.imgW, size.imgH);
    var shortSide = Math.min(size.imgW, size.imgH);
    var imgW = portrait ? shortSide : longSide;
    var imgH = portrait ? longSide : shortSide;

    var showMat = !!opts.mat && !unframed && size.mat > 0;
    var matEachIn = showMat ? (size.mat - MAT_OVERLAP) : 0;
    var frameThickIn = unframed ? 0 : 0.75;

    var finishedW = imgW + matEachIn * 2 + frameThickIn * 2;
    var finishedH = imgH + matEachIn * 2 + frameThickIn * 2;

    // Anchor the scale to the LARGEST size offered for this product (derived
    // from the visible size cards via getAnchorSize, so a 2:3 product anchors
    // to 40X60, a 4:5 product to 40X50, a 1:1 product to 36X36) rather than
    // fitting each size to the slot. Fitting every size to the slot makes a
    // small size scale UP to fill the same space, which makes its fixed-width
    // frame rails read proportionally thicker. Deriving the scale from the
    // anchor's finished dimensions keeps the per-inch scale identical for
    // every size, so frame thickness stays visually constant and a smaller
    // size simply renders smaller within the slot (an 8x12 genuinely is
    // smaller than a 40x60). Giant rectangles (40x60, 40x50) never mat, so
    // their mat inset is 0; the frame thickness is the same constant the
    // current size uses (0 when unframed). The anchor follows the art's
    // orientation so the fit math matches the rendered orientation.
    var anchorSize = getAnchorSize();
    var anchorLong = Math.max(anchorSize.imgW, anchorSize.imgH);
    var anchorShort = Math.min(anchorSize.imgW, anchorSize.imgH);
    var anchorImgW = portrait ? anchorShort : anchorLong;
    var anchorImgH = portrait ? anchorLong : anchorShort;
    var anchorFinishedW = anchorImgW + frameThickIn * 2;
    var anchorFinishedH = anchorImgH + frameThickIn * 2;

    // Measure the slot and fit the ANCHOR within it, minus a small margin so the
    // soft drop shadow is not clipped, taking whichever constraint is tighter so
    // the anchor never overflows. The margin scales gently with the slot (small
    // on a phone so mobile stays nearly full, larger on a wide desktop slot).
    // When nothing is measurable yet (hidden slot), fall back to maxPx against
    // the anchor; the ResizeObserver re-renders once the slot is visible.
    // Fill the slot orientation-aware, both anchored to the product's anchor
    // size finished dimensions so the per-inch scale stays constant. Portrait
    // fills the slot's height more conservatively (it is already tall, so a
    // gentler factor leaves breathing room top and bottom); landscape fills the
    // width more generously (a wide frame can run closer to the slot edges and
    // still look product-like). This replaces the earlier uniform 0.675 factor,
    // widening both orientations, landscape more than portrait.
    var FILL_PORTRAIT = 0.80;
    var FILL_LANDSCAPE = 0.89;
    var fillFactor = portrait ? FILL_PORTRAIT : FILL_LANDSCAPE;
    var avail = measureAvail(container);
    var scale;
    if (avail.w > 1 && avail.h > 1) {
      _lastSize = { w: avail.w, h: avail.h };
      var margin = Math.max(20, Math.min(44, Math.round(Math.min(avail.w, avail.h) * 0.06)));
      var fitW = Math.max(1, avail.w - margin * 2);
      var fitH = Math.max(1, avail.h - margin * 2);
      scale = Math.min(fitW / anchorFinishedW, fitH / anchorFinishedH) * fillFactor;
    } else {
      scale = (maxPx / Math.max(anchorFinishedW, anchorFinishedH)) * fillFactor;
    }

    // Current size renders with its own finished dimensions but the anchor's
    // scale, so its frame thickness matches every other size.
    var pxW = finishedW * scale, pxH = finishedH * scale;
    var ft = frameThickIn * scale;
    var matTh = matEachIn * scale;

    var stage = document.createElement("div");
    stage.className = "hjfp-stage";

    var frame = document.createElement("div");
    frame.className = "hjfp-frame" +
      (col && carveStrong(col) ? " carve" : "") +
      (col && col.family === "gold" ? " gold" : "");
    frame.style.width = pxW + "px";
    frame.style.height = pxH + "px";

    if (!unframed) {
      var rails = [
        { side: "top",    clip: "polygon(0 0, " + pxW + "px 0, " + (pxW - ft) + "px " + ft + "px, " + ft + "px " + ft + "px)", len: pxW, vertical: false },
        { side: "bottom", clip: "polygon(" + ft + "px " + (pxH - ft) + "px, " + (pxW - ft) + "px " + (pxH - ft) + "px, " + pxW + "px " + pxH + "px, 0 " + pxH + "px)", len: pxW, vertical: false },
        { side: "left",   clip: "polygon(0 0, " + ft + "px " + ft + "px, " + ft + "px " + (pxH - ft) + "px, 0 " + pxH + "px)", len: pxH, vertical: true },
        { side: "right",  clip: "polygon(" + (pxW - ft) + "px " + ft + "px, " + pxW + "px 0, " + pxW + "px " + pxH + "px, " + (pxW - ft) + "px " + (pxH - ft) + "px)", len: pxH, vertical: true }
      ];

      rails.forEach(function (r) {
        var rail = document.createElement("div");
        rail.className = "hjfp-rail " + r.side;
        rail.style.left = "0"; rail.style.top = "0";
        rail.style.width = pxW + "px"; rail.style.height = pxH + "px";
        rail.style.clipPath = r.clip;
        rail.style.webkitClipPath = r.clip;

        var grain = document.createElement("div");
        grain.className = "hjfp-grain";
        var L = r.len + ft * 2;
        grain.style.width = L + "px";
        grain.style.height = ft + "px";
        if (col) fillRail(grain, col);

        if (r.vertical) {
          var cx = r.side === "left" ? ft / 2 : pxW - ft / 2;
          grain.style.left = cx + "px";
          grain.style.top = pxH / 2 + "px";
          grain.style.transform = "translate(-50%,-50%) rotate(90deg)";
        } else {
          var cy = r.side === "top" ? ft / 2 : pxH - ft / 2;
          grain.style.left = pxW / 2 + "px";
          grain.style.top = cy + "px";
          grain.style.transform = "translate(-50%,-50%)";
        }
        rail.appendChild(grain);

        var lit = document.createElement("div");
        lit.className = "hjfp-lit";
        rail.appendChild(lit);

        frame.appendChild(rail);
      });
    }

    if (showMat) {
      var mat = document.createElement("div");
      mat.className = "hjfp-mat";
      mat.style.left = ft + "px"; mat.style.top = ft + "px";
      mat.style.width = (pxW - ft * 2) + "px";
      mat.style.height = (pxH - ft * 2) + "px";
      frame.appendChild(mat);
    }

    var art = document.createElement("div");
    art.className = "hjfp-art";
    var inset = ft + (showMat ? matTh : 0);
    art.style.left = inset + "px"; art.style.top = inset + "px";
    art.style.width = (pxW - inset * 2) + "px";
    art.style.height = (pxH - inset * 2) + "px";
    art.appendChild(img);
    frame.appendChild(art);

    stage.appendChild(frame);

    // Banana for scale: a fixed-size visual gag pinned above the frame. Its width
    // is the 8x12 print's short side (8 inches) at the engine scale, so it never
    // tracks the selected frame. On small frames it crowds the artwork's width; on
    // big frames it looks dwarfed. That mismatch is the joke. Only rendered when a
    // bananaUrl is supplied, so callers that omit it keep the original behavior.
    if (opts.bananaUrl) {
      // The shared `scale` derives from the anchor's FINISHED size, which folds in
      // frame thickness, so it runs ~2.5% larger when unframed (no rails) than when
      // framed. Using it for the banana would make the banana jump size as the
      // frame is toggled on and off. Recompute a frame-independent scale from the
      // anchor's bare ART size (frame thickness excluded), so the banana holds one
      // fixed on-screen size in every frame state. This only removes the unintended
      // framed/unframed jump; the gag (fixed banana, scaling frame) is unchanged.
      var bananaScale;
      if (avail.w > 1 && avail.h > 1) {
        var bMargin = Math.max(20, Math.min(44, Math.round(Math.min(avail.w, avail.h) * 0.06)));
        var bFitW = Math.max(1, avail.w - bMargin * 2);
        var bFitH = Math.max(1, avail.h - bMargin * 2);
        bananaScale = Math.min(bFitW / anchorImgW, bFitH / anchorImgH) * fillFactor;
      } else {
        bananaScale = (maxPx / Math.max(anchorImgW, anchorImgH)) * fillFactor;
      }
      // Decorative image (empty alt); the wrapping link carries the accessible
      // name, so the banana is announced once as a link rather than twice.
      var bananaImg = document.createElement("img");
      bananaImg.src = opts.bananaUrl;
      bananaImg.alt = "";
      bananaImg.setAttribute("aria-hidden", "true");
      // Explicit width/height match the served WebP variant (360x350) so the
      // element carries an aspect ratio for the browser to reserve space (CLS)
      // and to satisfy the "explicit width and height" audit. The visible size
      // is still driven by the CSS below (width:100% of the link, height:auto).
      bananaImg.width = 360;
      bananaImg.height = 350;
      bananaImg.style.display = "block";
      bananaImg.style.width = "100%";
      bananaImg.style.height = "auto";

      // Wrap the banana in a link, opened in a new tab. The link is the positioned
      // element and the hit area, so it takes the absolute placement, sizing, and
      // shadow; the image just fills it. rel includes noopener (hardens the new tab
      // against window.opener) and noreferrer.
      // Escalating gag, counted in _bananaClicks (module scope, so it survives the
      // re-renders that rebuild this element):
      //   click 1  -> opens r/BananasForScale
      //   click 2  -> opens the NPR duct-taped-banana story
      //   click 3+ -> rains banana emojis (no navigation)
      var BANANA_URL_FIRST = "https://www.reddit.com/r/BananasForScale/";
      var BANANA_URL_AGAIN = "https://www.npr.org/2024/11/21/nx-s1-5199568/a-duct-taped-banana-sells-for-6-2-million-at-an-art-auction";
      var bananaLink = document.createElement("a");
      bananaLink.target = "_blank";
      bananaLink.rel = "noopener noreferrer";
      // Point the link at the destination for the NEXT click, from the clicks so
      // far. Used by both pointer and keyboard activation, and on every re-render.
      var syncBanana = function () {
        if (_bananaClicks >= 2) {
          bananaLink.href = BANANA_URL_AGAIN; // fallback target when motion is reduced
          bananaLink.setAttribute("aria-label", "Make it rain bananas");
          bananaLink.title = "Make it rain bananas";
        } else if (_bananaClicks === 1) {
          bananaLink.href = BANANA_URL_AGAIN;
          bananaLink.setAttribute("aria-label", "A duct-taped banana sold for $6.2 million (NPR)");
          bananaLink.title = "A duct-taped banana sold for $6.2 million";
        } else {
          bananaLink.href = BANANA_URL_FIRST;
          bananaLink.setAttribute("aria-label", "Banana for scale on r/BananasForScale");
          bananaLink.title = "Banana for scale";
        }
      };
      syncBanana();
      bananaLink.addEventListener("click", function (e) {
        // Always handle the click ourselves so the CURRENT page never navigates:
        // the destinations open in a new tab via window.open, and the rain is
        // purely in-page. The href stays set (syncBanana) only so right-click ->
        // "open in new tab" and the hover status bar still show a sensible URL.
        e.preventDefault();
        _bananaClicks++;
        if (_bananaClicks === 1) {
          openInNewTab(BANANA_URL_FIRST);
        } else if (_bananaClicks === 2) {
          openInNewTab(BANANA_URL_AGAIN);
        } else if (prefersReducedMotion()) {
          // Reduced motion: skip the rain and open the NPR story instead, so the
          // click is never a dead end.
          openInNewTab(BANANA_URL_AGAIN);
        } else {
          rainBananas();
        }
        // Re-point the link for the click AFTER this one (also covers re-renders).
        syncBanana();
      });
      bananaLink.style.position = "absolute";
      bananaLink.style.display = "block";
      bananaLink.style.width = (8 * bananaScale) + "px";
      // The frame is flex-centered in the stage, so its top edge sits
      // (stageHeight - pxH) / 2 from the stage top. Anchoring the banana's BOTTOM
      // edge at 50% + pxH/2 + 30px places it 30px above the frame top without
      // needing the banana's natural height (unknown until the image loads).
      bananaLink.style.left = "50%";
      bananaLink.style.bottom = "calc(50% + " + (pxH / 2 + 30) + "px)";
      bananaLink.style.transform = "translateX(-50%)";
      // Soft directional drop-shadow (down and to the right) grounds the banana
      // against the wall. drop-shadow follows the banana's actual silhouette,
      // unlike box-shadow which would shadow the rectangular bounding box.
      bananaLink.style.filter = "drop-shadow(2px 4px 6px rgba(0, 0, 0, 0.18))";
      bananaLink.style.cursor = "pointer";
      bananaLink.style.zIndex = "2";
      bananaLink.appendChild(bananaImg);
      stage.appendChild(bananaLink);
    }

    container.innerHTML = "";
    container.appendChild(stage);
  }

  global.HJFramePreview = { render: render, SIZES: SIZES, COLORS: COLORS };
})(window);
